(() => {
  'use strict';

  const normalise = (value) => String(value || '')
    .toLocaleLowerCase('nl')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  /* De bron van de fondslijst gebruikt de bibliografische schrijfwijze
     "Achternaam, Voornaam". Laat die uitsluitend in de fondslijst intact;
     elders op de site blijven de gebruikelijke makersnamen ongewijzigd. */
  const displayName = (value) => String(value || '');

  const formatNameCell = (cell) => {
    if (!cell) return;
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const formatted = displayName(node.nodeValue);
      if (formatted !== node.nodeValue) node.nodeValue = formatted;
    });
    cell.dataset.mbNameFormatted = 'true';
  };

  const tableRows = (section) => {
    const head = section.querySelector('.mbf-header');
    return [...new Set([head, ...section.querySelectorAll('.mbf-row')].filter(Boolean))];
  };

  const markCreatorCells = (row, isHeader = false) => {
    if (!row) return null;
    const cells = [...row.children].filter((cell) => cell.nodeType === Node.ELEMENT_NODE);
    let author = row.querySelector('[data-mb-fund-role="author"]');
    let illustrator = row.querySelector('[data-mb-fund-role="illustrator"]');
    if (!author || !illustrator) {
      /* De live fondslijst gebruikt o.a. mbf-number, mbf-year, mbf-author en
         mbf-artist; niet iedere cel bevat letterlijk het woord "cell". */
      if (cells.length < 4) return null;
      author = cells[2];
      illustrator = cells[3];
      author.dataset.mbFundRole = 'author';
      illustrator.dataset.mbFundRole = 'illustrator';
    }
    if (isHeader) {
      if (author.textContent !== 'Auteur') author.textContent = 'Auteur';
      if (illustrator.textContent !== 'Illustrator') illustrator.textContent = 'Illustrator';
      const category = row.querySelector('.mbf-category, [data-mb-fund-role="category"]') || cells[4];
      if (category && category.textContent !== "Thema's en genres") category.textContent = "Thema's en genres";
    } else {
      formatNameCell(author);
      formatNameCell(illustrator);
    }
    return { author, illustrator };
  };

  const putCreatorsInOrder = (row, illustratorFirst, isHeader = false) => {
    const cells = markCreatorCells(row, isHeader);
    if (!cells) return;
    const { author, illustrator } = cells;
    if (!isHeader) {
      author.dataset.label = 'Auteur';
      illustrator.dataset.label = 'Illustrator';
    }
    /* De oorspronkelijke fondslijst geeft deze cellen vaste CSS-posities. Alleen
       DOM-elementen verplaatsen is daarom niet genoeg: zet ook de gridkolom vast. */
    if (window.matchMedia('(min-width: 769px)').matches) {
      author.style.setProperty('grid-column', illustratorFirst ? '4' : '3', 'important');
      illustrator.style.setProperty('grid-column', illustratorFirst ? '3' : '4', 'important');
    } else {
      author.style.removeProperty('grid-column');
      illustrator.style.removeProperty('grid-column');
    }
    if (illustratorFirst && author.previousElementSibling !== illustrator) author.before(illustrator);
    if (!illustratorFirst && illustrator.previousElementSibling !== author) illustrator.before(author);
  };

  const setupFundTable = (section) => {
    const rows = tableRows(section);
    if (!rows.length) return;
    const sort = [...section.querySelectorAll('select')].find((select) =>
      [...select.options].some((option) => normalise(`${option.value} ${option.textContent}`).includes('illustrator'))
    );
    const rowHost = section.querySelector('.mbf-list') || rows[0].parentElement;
    const search = section.querySelector('[data-mb-fund-search], .mbf-input, .mbf-tools input, input[type="search"]');
    const apply = () => {
      const selected = sort?.options?.[sort.selectedIndex];
      const currentRows = tableRows(section);
      const sortedByIllustrator = normalise(`${sort?.value || ''} ${selected?.textContent || ''}`).includes('illustrator');
      const query = normalise(search?.value || '');
      const illustratorMatch = query && currentRows.some((row) => !row.classList.contains('mbf-header') && normalise(markCreatorCells(row)?.illustrator?.textContent).includes(query));
      const authorMatch = query && currentRows.some((row) => !row.classList.contains('mbf-header') && normalise(markCreatorCells(row)?.author?.textContent).includes(query));
      const illustratorFirst = sortedByIllustrator || (illustratorMatch && !authorMatch);
      currentRows.forEach((row) => putCreatorsInOrder(row, illustratorFirst, row.classList.contains('mbf-header')));
      section.dataset.mbFundCreatorOrder = illustratorFirst ? 'illustrator-first' : 'author-first';
    };

    section.addEventListener('change', (event) => {
      if (event.target === sort) {
        requestAnimationFrame(apply);
        window.setTimeout(apply, 80);
        window.setTimeout(apply, 300);
      }
    });
    section.addEventListener('input', (event) => {
      if (event.target === search) {
        requestAnimationFrame(apply);
        window.setTimeout(apply, 80);
        window.setTimeout(apply, 300);
      }
    });
    window.addEventListener('resize', apply, { passive: true });
    if (rowHost) {
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          apply();
        });
      }).observe(rowHost, { childList: true, subtree: true });
    }
    apply();
  };

  const applyFirstProductImages = async (cards) => {
    cards.forEach((card) => {
      const image = card.querySelector('.mb-fund-list-card__image img');
      if (image) image.style.visibility = 'hidden';
    });
    const products = [];
    const root = window.Shopify?.routes?.root || '/';
    for (let page = 1; page <= 10; page += 1) {
      const response = await fetch(`${root}products.json?limit=250&page=${page}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Productafbeeldingen konden niet worden geladen (${response.status})`);
      const batch = (await response.json()).products || [];
      products.push(...batch);
      if (batch.length < 250) break;
    }
    const firstImageByHandle = new Map(products.map((product) => [product.handle, product.images?.[0]]));
    cards.forEach((card) => {
      const handle = card.querySelector('[data-product-handle]')?.dataset.productHandle;
      const firstImage = firstImageByHandle.get(handle);
      const image = card.querySelector('.mb-fund-list-card__image img');
      if (!image) return;
      if (!firstImage?.src) {
        image.remove();
        return;
      }
      image.removeAttribute('srcset');
      image.src = firstImage.src;
      image.alt = firstImage.alt || image.alt;
      image.style.visibility = '';
    });
  };

  const setup = (section) => {
    if (section.dataset.mbReady === 'true') return;
    setupFundTable(section);
    const search = section.querySelector('[data-mb-fund-search]');
    const count = section.querySelector('[data-mb-fund-count]');
    const cards = [...section.querySelectorAll('.mb-fund-list-card')];
    if (!search || !count || !cards.length) {
      section.dataset.mbReady = 'true';
      return;
    }

    cards.forEach((card) => { card.dataset.search = normalise(card.dataset.search); });
    const filter = () => {
      const query = normalise(search.value.trim());
      let visible = 0;
      cards.forEach((card) => {
        const match = !query || card.dataset.search.includes(query);
        card.hidden = !match;
        if (match) visible += 1;
      });
      count.textContent = `${visible} van ${cards.length} uitgaven`;
    };
    search.addEventListener('input', filter);
    filter();
    applyFirstProductImages(cards).catch((error) => {
      cards.forEach((card) => {
        const image = card.querySelector('.mb-fund-list-card__image img');
        image?.remove();
      });
      console.error(error);
    });
    section.dataset.mbReady = 'true';
  };

  const setupAll = (root = document) => {
    const sections = new Set(root.querySelectorAll('[data-mb-fund-list-section], .mb-page-content[data-page-handle="fondslijst"], #mb-fund, #mb-fonds'));
    root.querySelectorAll('.mbf-list').forEach((list) => sections.add(list.closest('.mb-page-content, #mb-fund, #mb-fonds') || list.parentElement));
    sections.forEach(setup);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupAll(), { once: true });
  else setupAll();
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
